import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { PALS_BY_INDEX } from "@/lib/breeding";
import { PalAvatar } from "@/components/PalAvatar";
import { Dna, Loader2, Check, ArrowRight, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ordered breeding steps: "Parent A + Parent B → enfant". Post-order = valid dependency order.
function planSteps(root) {
  const steps = [];
  const seen = new Set();
  const walk = (n) => {
    if (!n || !n.children) return;
    n.children.forEach(walk);
    if (!seen.has(n.idx)) {
      seen.add(n.idx);
      const [c0, c1] = n.children;
      steps.push({
        child: n.idx, a: c0.idx, b: c1.idx,
        aOwn: !c0.children && c0.own, bOwn: !c1.children && c1.own,
      });
    }
  };
  walk(root);
  return steps;
}

export default function SharedPlan() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    axios.get(`${API}/breeding/plan/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] text-center p-6">
        <div>
          <Dna className="w-10 h-10 mx-auto mb-3 text-red-400/70" />
          <p className="text-muted-foreground">Ce plan de breeding est introuvable ou a expiré.</p>
          <Link to="/" className="mt-4 inline-block text-primary hover:underline text-sm">Aller sur KSOS Hub</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] text-primary"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  const target = PALS_BY_INDEX[data.target];
  const steps = planSteps(data.tree);
  const stepNo = new Map();
  steps.forEach((s, i) => stepNo.set(s.child, i + 1));

  const Parent = ({ idx, own }) => {
    const p = PALS_BY_INDEX[idx];
    if (!p) return null;
    return (
      <div className="flex items-center gap-1.5 min-w-0">
        <PalAvatar pal={p} size={28} showElement />
        <span className="text-sm font-medium truncate">{p.fr}</span>
        {own ? (
          <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold bg-green-500/15 text-green-400 border border-green-500/40 shrink-0"><Check className="w-2.5 h-2.5" /> dispo</span>
        ) : stepNo.has(idx) ? (
          <span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold bg-primary/15 text-primary border border-primary/40 shrink-0">étape {stepNo.get(idx)}</span>
        ) : null}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] text-foreground py-10 px-4" data-testid="shared-plan-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-primary/70">KSOS Hub · Plan de breeding partagé</div>
          <h1 className="font-head text-3xl font-bold mt-1 flex items-center justify-center gap-2">
            <Dna className="w-7 h-7 text-primary" /> Obtenir {target?.fr}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Partagé par <span className="text-foreground font-semibold">{data.author}</span> ·{" "}
            {data.steps === 0 ? "déjà disponible" : `${data.steps} génération${data.steps > 1 ? "s" : ""}`}
          </p>
        </div>

        <div className="rounded-xl border border-border bg-[hsl(var(--surface))] overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-black/20">
            {target && <PalAvatar pal={target} size={44} showElement />}
            <div className="font-head font-semibold">{target?.fr}</div>
          </div>
          <div className="p-4 overflow-x-auto">
            {steps.length === 0 ? (
              <div className="text-sm text-muted-foreground">Ce Pal est déjà disponible — aucune reproduction nécessaire.</div>
            ) : (
              <ol className="space-y-2">
                {steps.map((s, i) => {
                  const child = PALS_BY_INDEX[s.child];
                  const isTarget = s.child === data.target;
                  return (
                    <li key={s.child} className={`rounded-lg border p-2.5 ${isTarget ? "border-primary/60 bg-primary/5" : "border-border bg-black/20"}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="w-6 h-6 shrink-0 rounded-full bg-primary/15 border border-primary/40 text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span>
                        <Parent idx={s.a} own={s.aOwn} />
                        <Plus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <Parent idx={s.b} own={s.bOwn} />
                        <ArrowRight className="w-4 h-4 text-primary shrink-0" />
                        <div className="flex items-center gap-1.5 min-w-0">
                          <PalAvatar pal={child} size={30} showElement />
                          <span className={`text-sm font-bold truncate ${isTarget ? "text-primary" : ""}`}>{child?.fr}</span>
                          {isTarget && <span className="shrink-0 text-xs">🎯</span>}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" data-testid="shared-plan-cta" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity">
            Créer ton propre plan sur KSOS Hub <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
